<template>
  <div class="post__intro">
    <div class="slot">
      <slot />
    </div>
    <div
      ref="menu"
      :class="{ 'collapse-contents': shouldCollapseContents }"
    >
      <section class="post__contents">
        <span class="post__contents-title">Содержание</span>
        <TableOfContentsItem
          v-for="item in contentsTokens"
          :key="item.id"
          :item="item"
          :level="0"
        />
      </section>
      <ui-button
        v-if="shouldCollapseContents"
        class="view-more-contents-btn"
        data-test="view-more-contents"
        text="Развернуть"
        :variant="UiButtonVariant.OUTLINE"
        :color-mode="UiButtonColorMode.NEUTRAL"
        :size="UiButtonSize.S"
        @clicked="openModal"
      />
    </div>
    <div
      v-if="isViewShowMoreBlock"
      class="showMore"
      @click="openModal"
    >
      <div class="title">Содержание</div>
      <IconBase
        name="article-icon"
        size="14"
      />
    </div>
    <ui-modal
      :is-shown="isModalShow"
      is-footer-hidden
      size="l"
      title="Содержание"
      @close="closeModal"
    >
      <template #content>
        <section
          class="post__contents-modal"
          @click="closeModal"
        >
          <TableOfContentsItem
            v-for="item in contentsTokens"
            :key="item.id"
            :item="item"
            :level="0"
          />
        </section>
      </template>
    </ui-modal>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onBeforeUnmount, inject, nextTick, watch } from 'vue';
  import { UiButton, UiButtonVariant, UiButtonColorMode, UiButtonSize } from '@experience/core-button-ui';
  import { UiModal } from '@experience/core-modal-ui';
  import { useScreenSize } from '@/shared/utils/breakpoints';
  import { IconBase } from '@/shared/ui/iconBase';
  import TableOfContentsItem from './TableOfContentsItem.vue';
  import type { ContentToken } from './tableOfContents.types';

  const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);

  const props = defineProps<{
    contentsTokens: ContentToken[];
  }>();

  const { isMobile, isTablet } = useScreenSize();

  type ObserverData = {
    observer: IntersectionObserver | null;
    value: boolean | null;
  };

  const menu = ref<HTMLElement | null>(null);
  const isModalShow = ref(false);
  const observerPostDescription = ref<ObserverData>({
    observer: null,
    value: null,
  });
  const observerContentsItems = ref<Record<string, ObserverData>>({});
  const countItems = (items: ContentToken[]): number => {
    return items.reduce((count, item) => {
      return count + 1 + (item.children ? countItems(item.children) : 0);
    }, 0);
  };

  const contentsLength = computed(() => countItems(props.contentsTokens || []));
  const isMobileOrTablet = computed(() => isMobile.value || isTablet.value);
  const hasContents = computed(() => contentsLength.value > 0);
  const hasLongContents = computed(() => contentsLength.value > 10);
  const shouldCollapseContents = computed(() => hasLongContents.value && isMobileOrTablet.value);
  const hasContentsItemsObservers = computed(() => Object.keys(observerContentsItems.value).length > 0);
  const hasVisibleContentsItem = computed(() => {
    return Object.values(observerContentsItems.value).some((observerData) => observerData.value === true);
  });
  const isViewShowMoreBlock = computed(() => {
    const isPostDescriptionVisible = observerPostDescription.value.value === true;
    return (
      hasContents.value && hasContentsItemsObservers.value && isPostDescriptionVisible && !hasVisibleContentsItem.value
    );
  });

  const openModal = () => {
    isModalShow.value = true;
  };

  const closeModal = () => {
    isModalShow.value = false;
  };

  const handleIntersect = (entries: IntersectionObserverEntry[], obj: ObserverData) => {
    obj.value = entries?.[0]?.intersectionRatio > 0;
  };

  const initObserver = (
    obj: ObserverData,
    node: Element | null,
    callback: (entries: IntersectionObserverEntry[], obj: ObserverData) => void,
  ) => {
    if (!node) {
      return;
    }

    obj.observer = new IntersectionObserver((entries) => {
      callback(entries, obj);
    });

    obj.observer.observe(node);
  };

  const findNodeForObserver = (selector: string) => {
    const nodeFromShadowRoot = shadowRoot?.querySelector(selector);
    if (nodeFromShadowRoot) {
      return nodeFromShadowRoot;
    }

    return document.querySelector(selector);
  };

  const initObserverForPostDescription = () => {
    if (!observerPostDescription.value.observer) {
      const postDescriptionNode = findNodeForObserver('.post-description');
      initObserver(observerPostDescription.value, postDescriptionNode, handleIntersect);
    }
  };

  const initObserverForContentsItems = () => {
    const menuNode = menu.value;
    if (!menuNode) {
      return;
    }

    Object.values(observerContentsItems.value).forEach((observerData) => {
      observerData.observer?.disconnect();
    });
    observerContentsItems.value = {};

    const contentsItemNodes = menuNode.querySelectorAll('.post__contents-item');
    contentsItemNodes.forEach((contentsItemNode, index) => {
      const observerKey = String(index);
      observerContentsItems.value[observerKey] = {
        observer: null,
        value: null,
      };
      initObserver(observerContentsItems.value[observerKey], contentsItemNode, handleIntersect);
    });
  };

  const initMainObservers = () => {
    initObserverForPostDescription();
    initObserverForContentsItems();
  };

  onMounted(async () => {
    await nextTick();
    initMainObservers();
  });

  watch(
    contentsLength,
    async () => {
      await nextTick();
      initMainObservers();
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    observerPostDescription.value?.observer?.disconnect();
    Object.values(observerContentsItems.value).forEach((observerData) => {
      observerData.observer?.disconnect();
    });
  });
</script>

<style lang="scss">
  @import '../../../app/styles/post_page';

  .slot {
    display: flex;
    flex-direction: column;
  }
</style>
